import { VisibilityConditionsData } from '@/typings/interfaces/data/conditions/visibilityConditions';
import {
  AlignContentType,
  BackgroundType,
  BorderType,
  DividerTypes,
  GradientPositionType,
  GradientType,
  OverlayType,
  PositionType,
  VideoFormatType,
  TextTransformType,
  TextStyleType,
  TextDecorationType,
  SliderTypes,
  ContentPositionType,
  HorizontalPositionType,
  HeightType,
  RepeatType,
  AdvancedWidthType,
  AdvancedPositionType,
  AdvancedHorizontalAlignmentType,
  AdvancedVerticalAlignmentType,
  DividerPositionType,
  GapType,
  ActionType
} from '@/typings/enums/enums';
import { ActionData } from '@/typings/interfaces/data/settings/actions';
import { TransitionTypes } from '@/typings/types/types';
import { DeviceData } from '@/hooks/useDevice';

export interface HeightData {
  width_type: 'grid' | 'full_width';
  width: string;
  gap: GapType;
  type: HeightType;
  fixed: string;
  minimum: string;
  content_position: ContentPositionType;
  overwrite_tablet?: string;
  overwrite_mobile?: string;

  // Popup specific properties on the height data.
  overlay?: string;
  overlay_bgcolor?: string;
  overlay_action?: ActionData;
}

export interface LayoutData {
  width?: string;
  alignment?: AlignContentType;
  content_position?: ContentPositionType;
  horizontal_position?: HorizontalPositionType;
  overwrite_tablet?: string;
  overwrite_mobile?: string;
}

export interface BackgroundData {
  background_type: BackgroundType;
  color: string;
  video?: {
    type?: VideoFormatType;
    url: string;
    disable_looping: string;
    opacity: string;
    fallback_image: string;
  };
  image?: {
    src: string;
    behavior: 'tile' | 'actual_size' | 'stretch' | 'contain' | 'cover' | 'center';
    tile_repeat: RepeatType;
    position: PositionType;
    x_position: string;
    y_position: string;
    opacity: string;
  };
  slider?: {
    images: Array<string>;
    interval: {
      enabled: string;
      time: string;
    };
    transition: SliderTypes;
    transition_time: string;
    shuffle: string;
  };
  background_to_section: string;
  overwrite_tablet?: string;
  overwrite_mobile?: string;
}

export interface OverlayData {
  overlay_to_section?: '0' | '1';
  overlay_type: OverlayType;
  overlay_color: string;
  location_1: string;
  color_2: string;
  location_2: string;
  gradient_type: GradientType;
  gradient_angle: string;
  gradient_position: GradientPositionType;
  overwrite_tablet?: string;
  overwrite_mobile?: string;
}

export interface BoxShadowData {
  color: string;
  blur: string;
  spread: string;
  horizontal: string;
  vertical: string;
  overwrite_tablet?: string;
  overwrite_mobile?: string;
}

export interface TextShadowData {
  color: string;
  blur: string;
  horizontal: string;
  vertical: string;
  overwrite_tablet?: string;
  overwrite_mobile?: string;
}

export interface ContentColorData {
  use_default_text_color: string;
  color: string;
  overwrite_tablet?: string;
  overwrite_mobile?: string;
}

export interface DividerData {
  enabled: string;
  position: DividerPositionType;
  type: DividerTypes;
  color: string;
  width: string;
  height: string;
  flip: string;
  invert: string;
  front: string;
  overwrite_tablet?: string;
  overwrite_mobile?: string;
}

export interface BorderData {
  type: BorderType;
  color: string;
  width: {
    top: string;
    right: string;
    bottom: string;
    left: string;
  };
  overwrite_tablet?: string;
  overwrite_mobile?: string;
}

export interface AdvancedStyleData {
  margin?: {
    top: string;
    right: string;
    bottom: string;
    left: string;
  };
  padding?: {
    top: number;
    right: number;
    bottom: number;
    left: number;
  };
  width?: {
    type: AdvancedWidthType;
    custom: string;
  };
  min_height?: string;
  horisontal_alignment?: string;
  overwrite_tablet?: string;
  overwrite_mobile?: string;
  customcss: CustomCSSData;
}

export interface TransitionsData {
  enter?: TransitionTypes;
  enter_duration?: string;
  enter_delay?: string;
  leave?: TransitionTypes;
  leave_duration?: string;
  leave_delay?: string;
  overwrite_tablet?: string;
  overwrite_mobile?: string;
}

//************* Exports ************///

export interface PositionData {
  position: AdvancedPositionType;
  horizontal?: {
    offset: string;
    alignment?: AdvancedHorizontalAlignmentType;
  };
  vertical?: {
    offset: string;
    alignment?: AdvancedVerticalAlignmentType;
  };
  overwrite_tablet?: string;
  overwrite_mobile?: string;
}

export interface CustomCSS {
  code: string;
  overwrite_tablet?: string;
  overwrite_mobile?: string;
}

export interface CustomCSSData {
  desktop: CustomCSS;
  tablet?: CustomCSS;
  mobile?: CustomCSS;
}

export interface AdvancedElementData {
  zindex: string;
  element_id: string;
  classname: string;
}

export interface SettingsData<Advanced extends SettingsAdvancedData = SettingsAdvancedData> {
  basic?: SettingsBasicData;
  style?: SettingsStyleData;
  advanced?: Advanced;
  show_on_desktop?: string;
  show_on_mobile?: string;
}

export enum PopupPositionType {
  TOP_LEFT = 'top_left',
  TOP_CENTER = 'top_center',
  TOP_RIGHT = 'top_right',
  CENTER_CENTER = 'center_center',
  BOTTOM_LEFT = 'bottom_left',
  BOTTOM_CENTER = 'bottom_center',
  BOTTOM_RIGHT = 'bottom_right'
}

export interface PopupPositionData {
  type: PopupPositionType;
  x: number | string;
  y: number | string;
}

export interface SettingsBasicData {
  height?: {
    desktop: HeightData;
    tablet?: HeightData;
    mobile?: HeightData;
  };
  name?: string;
  layout?: {
    desktop: LayoutData;
    tablet?: LayoutData;
    mobile?: LayoutData;
  };
  action?: ActionData;
  position?: DeviceData<PopupPositionData>;
}

export interface SettingsStyleData {
  background?: {
    desktop: BackgroundData;
    tablet?: BackgroundData;
    mobile?: BackgroundData;
  };
  overlay?: {
    desktop: OverlayData;
    tablet?: OverlayData;
    mobile?: OverlayData;
  };
  border?: {
    desktop: BorderData;
    tablet?: BorderData;
    mobile?: BorderData;
  };
  shadow?: {
    desktop: BoxShadowData;
    tablet?: BoxShadowData;
    mobile?: BoxShadowData;
  };
  content?: {
    desktop: ContentColorData;
    tablet?: ContentColorData;
    mobile?: ContentColorData;
  };
  divider?: {
    desktop: DividerData;
    tablet?: DividerData;
    mobile?: DividerData;
  };
}

export interface SettingsAdvancedData {
  style?: {
    desktop: AdvancedStyleData;
    tablet?: AdvancedStyleData;
    mobile?: AdvancedStyleData;
  };
  advanced?: AdvancedElementData;
  transitions?: {
    desktop: TransitionsData;
    tablet?: TransitionsData;
    mobile?: TransitionsData;
  };
  redirect?: {
    delay: string;
    enabled: string;
    target: string;
    url: string;
    type: ActionType;
  };
  visibility_condition?: VisibilityConditionsData;
  customcss?: CustomCSSData;
  show_on_desktop?: string;
  show_on_tablet?: string;
  show_on_mobile?: string;
}

export interface TypographyData {
  alignment?: AlignContentType;
  color?: string;
  font_family?: string;
  font_weight?: string;
  gradient_1?: string;
  gradient_2?: string;
  letter_spacing?: string;
  line_height?: string;
  size?: string;
  shape_color?: string;
  text_decoration?: TextDecorationType;
  rotate_text_color?: string;
  text_style?: TextStyleType;
  transform?: TextTransformType;
  overwrite_mobile?: string;
  overwrite_tablet?: string;
}

export interface AdvancedAddonsStyleData {
  generic?: {
    desktop: AdvancedStyleData;
    tablet?: AdvancedStyleData;
    mobile?: AdvancedStyleData;
    customcss?: CustomCSSData;
  };
  advanced?: AdvancedElementData;
  position?: {
    desktop: PositionData;
    tablet?: PositionData;
    mobile?: PositionData;
  };
  transitions?: {
    desktop: TransitionsData;
    tablet?: TransitionsData;
    mobile?: TransitionsData;
  };
}
